import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';

const PREFIX = 'AlertDialogs';

const classes = {
  root: `${PREFIX}-root`,
  vettingContainerTitle: `${PREFIX}-vettingContainerTitle`,
  vettingSection: `${PREFIX}-vettingSection`,
  vettingRow: `${PREFIX}-vettingRow`,
  vettingColumn: `${PREFIX}-vettingColumn`,
  vettingText: `${PREFIX}-vettingText`,
  footerBtns: `${PREFIX}-footerBtns`
};

const StyledDialog = styled(Dialog)((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    '& .MuiFormControl-root': {
      width: '100%',
    },
    '& .MuiFormControlLabel-root': {
      'margin-left': '0px',
      'flex-basis': '50%',
      'height': '100%',
      '&:last-child': {
        'margin-right': '0px',
      },
      '& .MuiTextField-root': {
        width: '100% !important',
      },
    },
    '& .Mui-error ~.MuiFormHelperText-root, & .Mui-error + label': {
      color: theme.palette.error.main,
    },
    '& .MuiInputBase-input:not(.MuiInputBase-inputMultiline)': {
      height: '100%',
    },
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(1.5, 3),
    },
    '& .MuiSelect-select': {
      height: [theme.spacing(7), '!important'],
      paddingTop: 0,
      paddingBottom: 0,
    },
  },

  [`& .${classes.vettingContainerTitle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  [`& .${classes.vettingSection}`]: {
    display: 'flex',
    flexFlow: 'column',
    padding: theme.spacing(3),
    overflowY: 'auto',
  },

  [`& .${classes.vettingRow}`]: {
    'display': 'flex',
    'margin': theme.spacing(1.5, 0),
    'flexFlow': 'row',
    'height': '100%',
    'justifyContent': 'center',
    'width': '100%',
    'alignItems': 'center',
    '&:first-child': {
      marginTop: 0,
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },

  [`& .${classes.vettingColumn}`]: {
    'display': 'flex',
    'flexDirection': 'column',
    'width': '100%',
    'justifyContent': 'center',
    'marginRight': theme.spacing(1),
    'height': '100%',
    '&:last-child': {
      marginRight: 0,
    },
  },

  [`& .${classes.vettingText}`]: {
    paddingLeft: theme.spacing(1),
  },

  [`& .${classes.footerBtns}`]: {
    marginLeft: theme.spacing(2),
  }
}));

export function DeleteProjectDialog({toggleDialog, handleDelete, open,projectId}) {


  const handleSubmit=()=>{
    handleDelete(projectId);
    toggleDialog();
  }

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return (
    <StyledDialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      onClick={handleClick}
      scroll="paper"
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="dashboard-dialog-title">
        <div className={classes.vettingContainerTitle}>
          <Typography variant='h6'>Are you sure you want to delete</Typography>
          <IconButton
            id='dialog-close'
            onClick={toggleDialog}
            edge='end'
            arial-label="Are you sure you want to delete"
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <div className={classes.vettingSection}>
          <div className={classes.vettingRow}>
            <div className={classes.vettingColumn}>
            <Alert severity="warning" className={classes.alert}>
                If you proceed, all associated users will be removed from this project.
            </Alert>
            </div>
          </div>
        </div>
      </DialogContent>

      <Divider />
      <DialogActions>
        <Button variant="outlined" color="primary"             
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
            }
          }}
          onClick={toggleDialog} 
          className={classes.footerBtns}
        >
          Cancel
        </Button>
        <Button variant="contained" color="primary" 
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              handleSubmit();
            }
          }} 
          onClick={handleSubmit} 
          className={classes.footerBtns}
        >
          Delete
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
DeleteProjectDialog.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.number.isRequired,
}

export function DeleteUserDialog({toggleDialog, handleDelete, open,userId}) {


  const handleSubmit=()=>{
    handleDelete(userId);
    toggleDialog();
  }

  const handleClick = (e) => {
    e.stopPropagation();
  };

  return (
    <Dialog
      onClose={toggleDialog}
      aria-labelledby="dashboard-dialog-title"
      open={open}
      className={classes.root}
      onClick={handleClick}
      scroll="paper"
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      <DialogTitle id="dashboard-dialog-title">
        <div className={classes.vettingContainerTitle}>
          <Typography variant='h6'>Are you sure you want to delete</Typography>
          <IconButton
            id='dialog-close'
            onClick={toggleDialog}
            edge='end'
            arial-label="Are you sure you want to delete"
            size="large">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <Divider />

      <DialogActions>
        <Button variant="outlined" color="primary"             
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              toggleDialog(e);
            }
          }}
          onClick={toggleDialog} 
          className={classes.footerBtns}
        >
          Cancel
        </Button>
        <Button variant="contained" color="primary" 
          onKeyPress={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter') {
              handleSubmit();
            }
          }} 
          onClick={handleSubmit} 
          className={classes.footerBtns}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
DeleteUserDialog.propTypes={
  toggleDialog: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  userId: PropTypes.number.isRequired,
}